import React, { useState, useEffect } from "react";
import { string, object, func, number } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";

import Button from "../../atoms/Button";
import LoginMobile from "../../atoms/icons/login";
import ContributorImage from "../../atoms/ContributorImage";
import PopupOverlay from "../../molecules/PopupOverlay";

import { logout, emailLink } from "../../helper/api";

import "./auth-view.m.css";
import { setMember, setJwtToken, setBookmark } from "../../helper/actions";

const { BroadcastChannel } = require("broadcast-channel");

const AuthView = ({
  pageType,
  member,
  currentPath,
  authHost,
  borderColor,
  textColor,
  sketchesHost,
  setMember,
  setJwtToken,
  setBookmark,
  clientId,
}) => {
  const [redirectUrl, setRedirectUrl] = useState(sketchesHost);
  const [showPopUp, setShowPopUp] = useState(false);
  const params = parseUrl(currentPath);
  const loginUrl =
    authHost +
    "/api/auth/v1/oauth/authorize?client_id=" +
    clientId +
    "&response_type=code&redirect_uri=" +
    authHost +
    "/api/auth/v1/oauth/token&callback_uri=" +
    authHost;

  const FallbackImage = () => <div styleName="first-letter">{member.email.charAt(0)}</div>;

  const getRedirectUrl = (defaultRedirectUrl) => {
    const redirectUrl = get(params, ["query", "redirect-url"], defaultRedirectUrl);
    setRedirectUrl(redirectUrl);
  };

  useEffect(() => {
    if (
      params.url.includes("sign-in-page") ||
      params.url.includes("sign-up-page") ||
      params.url.includes("forgot-password")
    ) {
      getRedirectUrl(sketchesHost);
    } else {
      params.url.includes("verify-email") ? getRedirectUrl(sketchesHost) : getRedirectUrl(global.location.href);
    }
  }, [params]);

  const sendVerificationLink = () => {
    emailLink(member.email, `${redirectUrl}`).then(() => {
      setShowPopUp(true);
      localStorage.setItem("redirectUrl", window.location.href);
    });
  };

  const logoutClick = () => {
    logout().then(() => {
      const channel = new BroadcastChannel("logout-channel");
      channel.postMessage("listen for logout");
      setMember(null);
      setJwtToken(null);
      setBookmark(null);
      window.location.href = localStorage.getItem("redirectUrl") || window.location.origin;
      localStorage.removeItem("redirectUrl");
    });
  };

  const isLogoutType = () => {
    if (
      pageType === "profile-page" ||
      pageType === "edit-profile-page" ||
      pageType === "change-password-page" ||
      pageType === "my-comments" ||
      pageType === "saved-page"
    ) {
      return true;
    }

    return false;
  };

  const changePopupView = () => setShowPopUp(false);

  // User not verified
  const notVerifiedView = () => {
    if (pageType === "verify-email-page") {
      return (
        <div styleName={`auth-wrapper`}>
          <Button
            content="Logout"
            buttonType="type4"
            onClick={logoutClick}
            className="btn logout-btn"
            buttonStyle={{ borderColor: borderColor, background: "transparent" }}
            textStyle={{ color: textColor }}
          />
        </div>
      );
    }

    return (
      <React.Fragment>
        {/* Not Verified User Popup Button */}
        <div styleName="auth-wrapper">
          <Button
            content="Verify Email"
            buttonType="type4"
            className="desktop-btn btn"
            onClick={sendVerificationLink}
            buttonStyle={{ borderColor: borderColor, background: "transparent" }}
            textStyle={{ color: textColor }}
          />
          {/* Mobile Login Popup Button */}
          <div styleName="mobile-btn btn-wrapper not-verified" onClick={sendVerificationLink}>
            {member["avatar-url"] ? <ContributorImage authorObj={member} memberProfilePic={true} /> : FallbackImage()}
          </div>
        </div>
        {showPopUp && (
          <PopupOverlay
            popupMessage={`We sent an activation email to you at ${member.email}. Please check your email box.`}
            onClickHandler={changePopupView}
          />
        )}
      </React.Fragment>
    );
  };

  // Verified User
  const loggedInView = () => {
    if (isLogoutType()) {
      // Show logout button if its profile page
      return (
        <div styleName={`auth-wrapper`}>
          <Button
            content="Logout"
            buttonType="type4"
            onClick={logoutClick}
            className="btn logout-btn"
            buttonStyle={{ borderColor: borderColor, background: "transparent" }}
            textStyle={{ color: textColor }}
          />
        </div>
      );
    }

    return (
      <React.Fragment>
        <a href="/profile">
          <div styleName={`auth-wrapper`}>
            <div styleName="btn-wrapper">
              {member["avatar-url"] ? <ContributorImage authorObj={member} memberProfilePic={true} /> : FallbackImage()}
            </div>
          </div>
        </a>
      </React.Fragment>
    );
  };

  // Not logged in User
  const loginView = () => {
    return (
      <React.Fragment>
        <a aria-label="avatar icon" href={loginUrl}>
          <div styleName={`auth-wrapper`}>
            <Button
              content="Login"
              buttonType="type4"
              className="desktop-btn btn"
              buttonStyle={{ borderColor: borderColor }}
              textStyle={{ color: textColor }}
            />
            <div styleName="mobile-btn" className="mobile-btn">
              <LoginMobile />
            </div>
          </div>
        </a>
      </React.Fragment>
    );
  };

  if (member) {
    return member["verification-status"] ? loggedInView() : notVerifiedView();
  }

  return loginView();
};

AuthView.propTypes = {
  pageType: string,
  member: object,
  currentPath: string,
  authHost: string,
  sketchesHost: string,
  setMember: func,
  setJwtToken: func,
  setBookmark: func,
  clientId: number,
};

const mapStateToProps = (state) => ({
  currentPath: get(state, ["qt", "currentPath"], ""),
  pageType: get(state, ["qt", "pageType"], null),
  member: state.member || null,
  authHost: get(state, ["qt", "currentHostUrl"], ""),
  clientId: get(state, ["qt", "config", "publisher-attributes", "sso_login", "client_id"], 0),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
});

const mapDispatchToProps = (dispatch) => ({
  setMember: (obj) => dispatch(setMember(obj)),
  setJwtToken: (flag) => dispatch(setJwtToken(flag)),
  setBookmark: (obj) => dispatch(setBookmark(obj)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthView);
